import { useEffect, useState } from "react";
import { Bell, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/App";
import { toast } from "sonner";
import {
  isPushSupported,
  hasNeverAsked,
  markAsked,
  subscribeToPush,
} from "@/utils/pushNotifications";

/**
 * Gentle banner that asks Admin users to enable push notifications for new orders.
 * - Shown once after first login post-update (when permission state is "default").
 * - Dismissed forever via localStorage flag (markAsked).
 * - Hidden on iOS browsers that don't support Web Push outside PWA mode.
 */
export default function PushPermissionBanner() {
  const { user, authAxios } = useAuth();
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    if (user.role !== "admin" && !(user.role === "agent" && user.push_enabled)) return;
    if (!isPushSupported()) return;
    if (Notification.permission !== "default") return; // already granted/denied
    if (user.role === "admin" && !hasNeverAsked()) return;
    // Slight delay so the banner doesn't pop in right at login
    const t = setTimeout(() => setVisible(true), 1200);
    return () => clearTimeout(t);
  }, [user]);

  const handleEnable = async () => {
    setBusy(true);
    try {
      const res = await subscribeToPush(authAxios);
      if (res.ok) {
        toast.success("Notifiche attivate — riceverai push sui nuovi ordini");
        setVisible(false);
      } else if (res.reason === "denied") {
        toast.error("Notifiche bloccate dal browser. Puoi attivarle dalle impostazioni Safari.");
        setVisible(false);
      } else if (res.reason === "unsupported") {
        toast.message("Le notifiche non sono supportate su questo browser");
        setVisible(false);
      } else {
        // subscribe_failed / save_failed / no_service_worker / vapid_unavailable
        toast.error(`Errore: ${res.reason}${res.error ? ` — ${res.error}` : ""}`, { duration: 8000 });
      }
    } catch (e) {
      toast.error(`Errore attivazione: ${e?.message || e}`);
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = () => {
    markAsked();
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      data-testid="push-permission-banner"
      className="fixed bottom-4 left-3 right-3 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-sm z-50 rounded-2xl border border-amber-500/30 bg-[#18181b]/95 backdrop-blur shadow-2xl p-4 flex items-start gap-3"
    >
      <div className="w-9 h-9 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 mt-0.5">
        <Bell className="w-4 h-4 text-amber-300" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-semibold leading-tight">
          {user?.role === "admin" ? "Ricevi push sui nuovi ordini" : "Attiva le notifiche"}
        </p>
        <p className="text-zinc-400 text-xs mt-1 leading-snug">
          {user?.role === "admin"
            ? "Una notifica appena un cliente o agente invia un ordine. Funziona anche con Triolo.Store chiuso, sia su iPhone che su Mac."
            : "Ricevi i messaggi dall'amministratore anche con l'app chiusa."}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <Button
            data-testid="push-enable-btn"
            size="sm"
            onClick={handleEnable}
            disabled={busy}
            className="bg-amber-500 hover:bg-amber-600 text-black font-medium h-8 px-3 text-xs"
          >
            {busy ? "Attivazione..." : "Attiva"}
          </Button>
          <Button
            data-testid="push-dismiss-btn"
            size="sm"
            variant="ghost"
            onClick={handleDismiss}
            className="text-zinc-400 hover:text-white h-8 px-3 text-xs"
          >
            Più tardi
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Chiudi"
        className="text-zinc-500 hover:text-zinc-300 -mt-1 -mr-1"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
